import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Button from 'shared/button';
import Modal from 'components/modal';

import AvailableOptions from 'components/multiselect_modal/options/available';
import Cards from 'bundles/apex/card/container';
import SelectedOptions from 'components/multiselect_modal/options/selected';
import SettingsContext from 'components/multiselect_modal/settings_context';
import { Form } from 'react-bootstrap-5';
import Select from 'react-select';
import Banner from 'shared/banner';
import { faCircleInfo, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';

import './styles.scss';
import './banner_varients.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { PAGE_PATHS } from './base_report';

export default function ColumnselectModal({
  defaultGroupingOptions,
  generatePdfOptions,
  generateType,
  initiallySelected,
  optionLabelName,
  options,
  optionsLabel,
  optionValueName,
  reorderable,
  saveButtonLabel = 'Save',
  saving,
  setGeneratedPdfOptions,
  title,
  onClose,
  onSave,
}) {
  const isGeneratingPdf = generateType === PAGE_PATHS.GENERATE_PDF || generateType === PAGE_PATHS.GENERATING_PDF;
  const [selection, setSelection] = useState(() => (
    isGeneratingPdf ? initiallySelected.filter(column => !column.locked) : initiallySelected
  ));

  const groupByOptions = defaultGroupingOptions.group_by;
  const sortByOptions = defaultGroupingOptions.sort_by;

  const maxColumnsForPdf = 12;
  const allowedOptions = isGeneratingPdf ? options.filter(column => column.pdfField) : options;
  const lockedValues = allowedOptions.filter(column => column.locked);
  const selectedPdfColumns = selection.filter(column => column.pdfField);

  const submit = () => isGeneratingPdf ? onSave(selectedPdfColumns) : onSave(selection);

  function renderSelect(options, key) {
    return (
      <Select
        classNamePrefix="select"
        isClearable={false}
        isMulti={false}
        isSearchable={false}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        options={options}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        value={generatePdfOptions[key]}
        onChange={(e) => {
          setGeneratedPdfOptions({
            ...generatePdfOptions,
            [key]: e
          });
        }}
      />
    );
  }

  function renderSelectOptions() {
    return (
      <div className="d-flex align-items-center justify-content-between">
        <Form.Group style={{ width: 'calc(50% - 1.5rem)' }}>
          <Form.Label>Sort By:</Form.Label>
          {renderSelect(sortByOptions, 'sort_by')}
        </Form.Group>
        <Form.Group style={{ width: 'calc(50% - 1.5rem)' }}>
          <Form.Label>Group By:</Form.Label>
          {renderSelect(groupByOptions, 'group_by')}
        </Form.Group>
      </div>
    );
  }

  const tooManyFieldsBanner = () => {
    const tooManyColumnsWarning = `Maximum of ${maxColumnsForPdf} fields allowed. Currently selected: \
      ${selectedPdfColumns.length}`;

    return (
      <div className="banner-container">
        <ColumnSelectBanner content={tooManyColumnsWarning} variant="tooManyFields"/>
      </div>
    );
  };

  const bannerVariants = {
    tooManyFields: {
      className: 'banner--too-many-fields',
      icon: faCircleXmark
    },
    unauthorizedPdfFields: {
      className: 'banner--unauthorized-fields',
      icon: faCircleInfo
    }
  };

  const ColumnSelectBanner = props => <Banner {...props} extraVariants={bannerVariants}/>;

  const unathorizedPdfFieldsBanner = () => {
    const content = (
      <div>
        Some fields you have selected are not yet available in the PDF report:
        <ul style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
          {unauthorizedPdfFields.map(column => (
            <li key={column.key}>{column.label}</li>
          ))}
        </ul>
        If these fields are essential, click "cancel" and generate this report as a CSV instead
      </div>
    );

    return (
      <ColumnSelectBanner content={content} variant="unauthorizedPdfFields"/>
    );
  };

  const updateSelection = (newSelection = []) => {
    const missingFromNewSelection = lockedValues.filter(item => !newSelection.includes(item));
    const newSelectionWithLocked = [...newSelection, ...missingFromNewSelection];
    setSelection(newSelectionWithLocked);
  };

  const unauthorizedPdfFields = selection.filter(column => !column.pdfField && !lockedValues.includes(column));

  return (
    <React.Fragment>
      <Modal.Header closeButton>
        {title}
      </Modal.Header>

      <Modal.Body className="flex-column">
        {isGeneratingPdf && selectedPdfColumns.length > maxColumnsForPdf && tooManyFieldsBanner()}
        {isGeneratingPdf && unauthorizedPdfFields.length > 0 && unathorizedPdfFieldsBanner()
        }
        <Cards className="flex-1 w-100">
          <SettingsContext.Provider value={{ optionLabelName, optionValueName }}>
            <AvailableOptions
              options={allowedOptions}
              optionsLabel={optionsLabel}
              selection={selection}
              onAdd={added => updateSelection([...selection, added])}
              onAddAll={() => updateSelection([
                ...selection, ...allowedOptions.filter(column => !selection.includes(column))
              ])}
            />

            <SelectedOptions
              options={allowedOptions}
              optionsLabel={optionsLabel}
              reorderable={reorderable}
              selection={selection}
              onChange={updateSelection}
              onRemoveAll={() => updateSelection([...allowedOptions.filter(column => column.locked)])}
            />
          </SettingsContext.Provider>
        </Cards>
        {isGeneratingPdf && renderSelectOptions()}
      </Modal.Body>

      <Modal.Footer>
        <Button.Cancel className="mr-2" onClick={onClose}/>
        <Button.Primary disabled={saving || !selection.length} onClick={submit}>
          {saving && <FontAwesomeIcon className="mr-2" icon={faSpinner} spin/>}
          {saveButtonLabel}
        </Button.Primary>
      </Modal.Footer>
    </React.Fragment>
  );
}

ColumnselectModal.propTypes = {
  defaultGroupingOptions: PropTypes.object.isRequired,
  generatePdfOptions: PropTypes.object.isRequired,
  generateType: PropTypes.string.isRequired,
  initiallySelected: PropTypes.array,
  optionLabelName: PropTypes.string,
  options: PropTypes.array.isRequired,
  optionsLabel: PropTypes.string,
  optionValueName: PropTypes.string,
  reorderable: PropTypes.bool,
  saveButtonLabel: PropTypes.string,
  saving: PropTypes.bool.isRequired,
  setGeneratedPdfOptions: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};
